<script setup lang="ts">
import { PhArrowRight, PhCaretDown, PhCaretUp } from '@phosphor-icons/vue'
import { computed, shallowRef } from 'vue'

import RouteCodeBadge from '@/components/transit/RouteCodeBadge.vue'
import type { Itinerary } from '@/services/routePlanner'
import { formatDistance, formatFare } from '@/utils/format'

import { itineraryFare } from './itineraryFare'

const props = defineProps<{
  itinerary: Itinerary
  /** routeId → route code, from the cached dataset. */
  routeCodes: Map<number, string>
  routeNames: Map<number, string>
  /** routeId → flat fare in dong. A route missing here has no known price. */
  routeFares: Map<number, number>
}>()

defineEmits<{ openRoute: [routeCode: string] }>()

const expanded = shallowRef(false)

/**
 * A leg whose route is absent from the offline dataset.
 *
 * `public/data/routes.json` is a periodic snapshot; the live planner returns
 * ids that postdate it (observed: id 4816 against a 164-route snapshot from
 * 2026-07-20). Rendering `String(routeId)` in a route badge was actively
 * misleading — "4816" is indistinguishable from a real code like "104", and no
 * such bus exists to look up.
 */
const UNKNOWN_ROUTE = '?'

const codes = computed(() =>
  props.itinerary.legs.map((leg) => props.routeCodes.get(leg.routeId) ?? UNKNOWN_ROUTE),
)

const hasUnknownRoute = computed(() =>
  props.itinerary.legs.some((leg) => !props.routeCodes.has(leg.routeId)),
)

function codeFor(index: number) {
  return props.routeCodes.get(props.itinerary.legs[index]!.routeId)
}

const minutes = computed(() => Math.round(props.itinerary.totalDurationSeconds / 60))

const transfers = computed(() => Math.max(0, props.itinerary.legs.length - 1))

const fare = computed(() => itineraryFare(props.itinerary.legs, props.routeFares))

/**
 * A leg the app CAN name but cannot price, i.e. its route is in the snapshot with
 * `fare: 0`. Split out from the unknown-route case because the two need different
 * explanations: the note below covers this one, and the existing unknown-route
 * note covers the other. Both can be true at once, on different legs.
 */
const hasUnpricedKnownRoute = computed(() =>
  props.itinerary.legs.some(
    (leg, index) => fare.value.perLeg[index] === undefined && props.routeCodes.has(leg.routeId),
  ),
)
</script>

<template>
  <div class="border-b">
    <button
      type="button"
      class="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted"
      :aria-expanded="expanded"
      @click="expanded = !expanded"
    >
      <span class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        <template v-for="(code, index) in codes" :key="`${code}-${index}`">
          <PhArrowRight v-if="index > 0" class="size-3.5 shrink-0 text-muted-foreground" />
          <RouteCodeBadge :code="code" size="sm" />
        </template>
      </span>

      <span class="shrink-0 text-right">
        <span class="block text-sm font-bold">
          <!-- A multi-leg plan has one leg reporting no duration, so the sum is a
               lower bound. Marking it beats quietly understating the journey. -->
          {{ itinerary.durationIsPartial ? '≥' : '' }}{{ minutes }} min
        </span>
        <span class="block text-xs text-muted-foreground">
          {{ transfers === 0 ? 'Direct' : `${transfers} transfer${transfers > 1 ? 's' : ''}` }}
          · {{ formatDistance(itinerary.totalDistanceMeters) }}
        </span>
        <!-- Flat fare per boarding, so a transfer is a second ticket. Hidden
             entirely when no leg could be priced — `≥0 đ` is worse than silence. -->
        <span v-if="fare.isKnown" class="block text-xs font-bold">
          {{ fare.isPartial ? '≥' : '' }}{{ formatFare(fare.total) }}
        </span>
      </span>

      <PhCaretUp v-if="expanded" class="size-4 shrink-0 text-muted-foreground" />
      <PhCaretDown v-else class="size-4 shrink-0 text-muted-foreground" />
    </button>

    <div v-if="expanded" class="border-t bg-muted px-3 py-3 text-xs">
      <p class="text-muted-foreground">
        Board at <span class="text-foreground">{{ itinerary.board.name }}</span>
      </p>

      <ul class="my-2 space-y-2">
        <li v-for="(leg, index) in itinerary.legs" :key="index" class="flex items-start gap-2">
          <RouteCodeBadge :code="codes[index]!" size="sm" />
          <span class="min-w-0 flex-1">
            <span class="block">
              {{ routeNames.get(leg.routeId) ?? 'Route not in the offline data' }}
            </span>
            <span class="block text-muted-foreground">
              {{ leg.alightStopIndex - leg.boardStopIndex }} stops ·
              {{ formatDistance(leg.distanceMeters) }}
              <template v-if="fare.perLeg[index] !== undefined">
                ·
                <span class="font-bold text-foreground">{{ formatFare(fare.perLeg[index]!) }}</span>
              </template>
            </span>
          </span>
          <!-- Only offered when the route resolves: /routes/<id> would just
               bounce off the unknown-route redirect. -->
          <button
            v-if="codeFor(index)"
            type="button"
            class="shrink-0 font-bold text-primary"
            @click.stop="$emit('openRoute', codeFor(index)!)"
          >
            Live buses
          </button>
        </li>
      </ul>

      <p class="text-muted-foreground">
        Get off at <span class="text-foreground">{{ itinerary.alight.name }}</span>
      </p>

      <p v-if="itinerary.durationIsPartial" class="mt-2 text-muted-foreground">
        The operator reports no time for one leg of this journey, so the total is a minimum.
      </p>

      <p v-if="hasUnknownRoute" class="mt-2 text-muted-foreground">
        One leg uses a route that is not in this app's offline data, so its number, fare and live
        buses are unavailable.
      </p>

      <p v-if="hasUnpricedKnownRoute" class="mt-2 text-muted-foreground">
        The operator publishes no fare for one leg of this journey, so the total is a minimum.
      </p>
    </div>
  </div>
</template>
